import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { PostModel } from '@/common/models/post'
import { api, ApiError, APIPaginated } from '@/lib/axios'

type getPaginateNewsHighlightedProps = {
  userId: string
}

async function getPaginateNewsHighlighted({
  userId,
}: getPaginateNewsHighlightedProps) {
  const { data } = await api.backend.get<APIPaginated<PostModel>>(
    '/posts/paginate',
    {
      params: {
        limit: 10,
        relations: {
          where: {
            isSlider: true,
            isActive: true,
            sliderSort: {
              gt: -1,
            },
          },
          orderBy: [{ sliderSort: 'asc' }, { updatedAt: 'desc' }],
          include: {
            PostsPinned: {
              where: {
                pinnedById: userId,
              },
            },
          },
        },
      },
    },
  )

  return data
}

export function useNewsHighlighted() {
  const { userInfo } = useGetUserInfo()

  const { t } = useTranslation('panel')

  const { data, isLoading, error } = useQuery<
    APIPaginated<PostModel>,
    AxiosError<ApiError>
  >({
    queryKey: ['/posts-slider/paginate', userInfo?.id],
    queryFn: () =>
      getPaginateNewsHighlighted({
        userId: userInfo?.id ?? '',
      }),
  })

  useEffect(() => {
    if (error) {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('news.use_paginate_posts_slider.error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('news.use_paginate_posts_slider.error.description'),
        },
      })
    }
  }, [error, t])

  return {
    newsHighlighted: data?.items || [],
    isLoadingNewsHighlighted: isLoading,
  }
}
